import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Header from "../components/Common/Header";
import {
  LocationSummary,
  LocationCreate,
  listLocations,
  createLocation,
  deleteLocation,
} from "../api/locations";
import { useVaultContext } from "../context/VaultContext";

export default function Locations() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { currentVaultId } = useVaultContext();

  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<LocationSummary | null>(null);

  // Form state for creating
  const [newLocation, setNewLocation] = useState<LocationCreate>({
    name: "",
    description: "",
    address: "",
    city: "",
    county: "",
    country: "",
    postal_code: "",
  });

  // Fetch locations (filtered by current vault)
  const { data: locations, isLoading, error } = useQuery({
    queryKey: ["locations", currentVaultId, search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listLocations(token, { 
        search: search || undefined, 
        limit: 100,
        vaultId: currentVaultId || undefined,
      });
    },
    enabled: !!currentVaultId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: LocationCreate) => {
      const token = await getAccessTokenSilently();
      return createLocation(token, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      setShowCreateModal(false);
      setNewLocation({
        name: "",
        description: "",
        address: "",
        city: "",
        county: "",
        country: "",
        postal_code: "",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      return deleteLocation(token, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      setDeleteConfirm(null);
    },
  });

  const handleCreate = () => {
    if (!newLocation.name) return;
    createMutation.mutate(newLocation);
  };

  const formatLocationDisplay = (loc: LocationSummary) => {
    const parts = [];
    if (loc.city) parts.push(loc.city);
    if (loc.county) parts.push(loc.county);
    if (loc.country) parts.push(loc.country);
    return parts.join(", ") || "No address";
  };

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/dashboard" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Dashboard
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-vault-900">
              Locations
            </h1>
            <p className="mt-1 text-vault-600">
              Specific places you can tag to people, documents, and media
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
          >
            + Add Location
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search locations..."
            className="w-full max-w-md rounded-lg border border-vault-200 px-4 py-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load locations. Please try again.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && locations && locations.length === 0 && (
          <div className="rounded-lg bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-vault-900">No locations yet</h3>
            <p className="mt-1 text-vault-600">
              Create specific locations like &ldquo;The Old Family Homestead&rdquo; or &ldquo;St. Mary&apos;s Church&rdquo;
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
            >
              Add Your First Location
            </button>
          </div>
        )}

        {/* List */}
        {locations && locations.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {locations.map((location) => (
              <div
                key={location.id}
                className="rounded-lg bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <Link to={`/locations/${location.id}`} className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        {location.geo_lat && location.geo_lng ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-vault-900 truncate">
                          {location.name}
                        </h3>
                        <p className="text-sm text-vault-500 truncate">
                          {formatLocationDisplay(location)}
                        </p>
                      </div>
                    </div>
                    {(location.media_count > 0 || location.person_count > 0) && (
                      <div className="mt-2 flex gap-3 text-xs text-vault-500">
                        {location.media_count > 0 && (
                          <span>{location.media_count} media</span>
                        )}
                        {location.person_count > 0 && (
                          <span>{location.person_count} people</span>
                        )}
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteConfirm(location);
                    }}
                    className="p-1 text-vault-400 hover:text-red-600"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">
              Add Location
            </h2>
            <p className="mb-4 text-sm text-vault-600">
              Create a specific location that can be tagged to people, documents, and photos.
            </p>

            {createMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {createMutation.error instanceof Error ? createMutation.error.message : "Failed to create"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., The Cottage at the end of Cave's Lane"
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Description
                </label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={2}
                  placeholder="Brief description of this location..."
                  value={newLocation.description}
                  onChange={(e) => setNewLocation({ ...newLocation, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Address
                </label>
                <input
                  type="text"
                  placeholder="Street address"
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={newLocation.address}
                  onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    City / Town
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newLocation.city}
                    onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    County / Region
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newLocation.county}
                    onChange={(e) => setNewLocation({ ...newLocation, county: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    Country
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newLocation.country}
                    onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={newLocation.postal_code}
                    onChange={(e) => setNewLocation({ ...newLocation, postal_code: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !newLocation.name}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {createMutation.isPending ? "Creating..." : "Create Location"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">
              Delete Location?
            </h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete{" "}
              <span className="font-medium">{deleteConfirm.name}</span>?
              This will remove all tags to this location.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
